import { useFormik } from 'formik'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, TabContent, TabPane } from 'reactstrap'
import * as Yup from 'yup'
import images from '../../../../api/images/hooks'
import offers from '../../../../api/offers/hooks'
import { MODAL_TYPES } from '../../../../common/constants'
import { hideModal, showModal } from '../../../../store/modal'
import { resetTableCheckbox } from '../../../../store/table'
import FormTabNavigation from '../../../Common/FormTabNavigation'
import Loader from '../../../Common/Loader'
import OfferBaseTab from '../components/OfferBaseTab'
import { useOfferBaseModalTabs, useOfferFormConfig } from '../hooks'
import { OfferBaseFormValues, OfferBaseModalProps, OfferModalItem } from '../types'
import { generateOfferSettingsGroup } from '../utils'

const OfferBaseModal = (props: OfferBaseModalProps) => {
    const dispatch = useDispatch()
    const { t } = useTranslation()

    const { OFFER_BASE_MODAL, OFFER_SEND_MODAL } = MODAL_TYPES
    const { isOpen, offerBaseModal } = props
    const { items, offerType, offerSettingsGroup, offerEmailSettingsGroup } = offerBaseModal

    const itemsIds = items.map((obj: OfferModalItem) => obj.id) as number[]

    const imagesConfig = {
        params: {
            parent: itemsIds,
            collection: 'photo',
            module: offerType,
        },
        isEnabled: { enabled: !!itemsIds && itemsIds.length > 0 },
    }

    const { tabs, activeTab, handleTabChange } = useOfferBaseModalTabs()
    const { inputs, validationConfig } = useOfferFormConfig(activeTab, offerType, offerEmailSettingsGroup)

    const { data: photos, isPending: isPhotosPending } = images.GetImages.useQuery(imagesConfig.params, imagesConfig.isEnabled)
    const { data: offerTemplates, isPending: isTemplatesPending } = offers.GetOfferTemplates.useQuery(offerType)
    const { mutate: previewOffer } = offers.PreviewOffer.useMutation()

    const formik = useFormik<{ offer: OfferBaseFormValues }>({
        enableReinitialize: true,
        initialValues: {
            offer: (validationConfig.initialValues ?? {}) as OfferBaseFormValues,
        },
        validationSchema: Yup.object({
            offer: validationConfig.validationSchema ?? Yup.object({}),
        }),
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onSubmit: () => {},
    })

    const { setFieldValue } = formik
    const { agent_id, offer_template, offer_locale, offer_settings_group, offer_email_settings_group } = formik.values.offer ?? {}

    // effect to sync offerSettingsGroup from props to Formik when OfferSettingsModal updates it
    useEffect(() => {
        if (offerSettingsGroup) {
            setFieldValue('offer.offer_settings_group', offerSettingsGroup)
        }
    }, [offerSettingsGroup, setFieldValue])

    const formattedItems: OfferModalItem[] = useMemo(() => {
        if (!photos) return items

        return items.map((item) => ({
            ...item,
            photos: photos.filter((photo) => photo.parent === item.id),
        }))
    }, [items, photos])

    const handleOfferPreview = () => {
        if (!agent_id || !offer_template || !offer_locale) {
            console.error('Missing required preview fields', { agent_id, offer_template, offer_locale })
            return
        }

        const offerSettingsGroup = generateOfferSettingsGroup(offer_settings_group || [], formattedItems)
        setFieldValue('offer.offer_settings_group', offerSettingsGroup)

        // showModal action is dispatched in hook onSuccess
        // previewCount starts with 1, rest is managed in OfferPreviewModal
        previewOffer({
            previewCount: 1,
            data: {
                agent_id,
                offer_template,
                offer_settings_group: offerSettingsGroup,
                offer_type: offerType,
                offer_locale,
            },
        })
    }

    const handleOfferSend = () => {
        if (!offer_template || !offer_locale || !agent_id || !offer_email_settings_group?.length) {
            console.error('Missing required offer fields', { offer_template, offer_locale, agent_id, offer_email_settings_group })
            return
        }

        const finalOfferSettings = offer_settings_group?.length ? offer_settings_group : generateOfferSettingsGroup([], formattedItems)

        const modalPayload = {
            modalType: OFFER_SEND_MODAL,
            modalProps: {
                items,
                offerType,
                offerEmailSettingsGroup: offer_email_settings_group || [],
                offerSettingsGroup: finalOfferSettings,
                offerTemplate: offer_template,
                offerLocale: offer_locale,
                agentId: agent_id,
            },
        }

        dispatch(showModal(modalPayload))
    }

    const handleClose = () => {
        dispatch(hideModal({ modalType: OFFER_BASE_MODAL }))
        dispatch(resetTableCheckbox(true))
        formik.resetForm()
    }

    return (
        <>
            <Modal isOpen={isOpen} centered>
                <ModalHeader toggle={handleClose}>
                    {/* TODO: refactor when FormTabNavigation is converted to TS  */}
                    <FormTabNavigation tabs={tabs} activeTab={activeTab} handleTabChange={handleTabChange} customClass="offer-print-modal-tabs">
                        {null}
                    </FormTabNavigation>
                </ModalHeader>
                <ModalBody style={{ minHeight: '500px' }}>
                    {isPhotosPending || isTemplatesPending ? (
                        <div style={{ height: '500px' }}>
                            <Loader />
                        </div>
                    ) : (
                        <TabContent activeTab={activeTab}>
                            <TabPane tabId="offer">
                                <OfferBaseTab
                                    activeTab={activeTab}
                                    formik={formik}
                                    inputs={inputs}
                                    offerTemplates={offerTemplates}
                                    items={formattedItems}
                                    offerEmailSettingsGroup={offerEmailSettingsGroup}
                                />
                            </TabPane>
                            <TabPane tabId="print">
                                <OfferBaseTab activeTab={activeTab} formik={formik} inputs={inputs} offerTemplates={offerTemplates} />
                            </TabPane>
                        </TabContent>
                    )}
                </ModalBody>
                <ModalFooter>
                    {activeTab === 'offer' ? (
                        <>
                            <Button color="warning" className="btn-label" onClick={handleOfferPreview}>
                                <i className="mdi mdi-eye align-bottom label-icon align-middle fs-16 me-2"></i>
                                {t('button.review')}
                            </Button>
                            <Button color="send" className="btn-label" onClick={handleOfferSend} disabled={!offer_email_settings_group?.length}>
                                <i className="mdi mdi-arrow-right align-bottom label-icon align-middle fs-16 me-2"></i>
                                {t('button.send')}
                            </Button>
                        </>
                    ) : (
                        <Button type="submit" color="send" className="btn-label" onClick={handleOfferPreview}>
                            <i className="mdi mdi-printer-outline align-bottom label-icon align-middle fs-16 me-2"></i>
                            {t('app.common.print')}
                        </Button>
                    )}
                </ModalFooter>
            </Modal>
        </>
    )
}

export default OfferBaseModal
